import React, { useEffect, useRef, useState } from 'react';
import { BannerIcon } from '../../ui/Banner/BannerIcon';
import type { BannerTone } from '../../ui/Banner/types';
import type { ToastProps } from './types';

export const DISMISS_DELAY_MS = 4000;
export const TRANSITION_MS = 300;

const BANNER_TONE: Record<ToastProps['type'], BannerTone> = {
    success: 'success',
    error: 'danger',
};

const TONE_CLASSES: Record<ToastProps['type'], string> = {
    success: 'border-success-100 bg-surface-success text-success',
    error: 'border-danger-100 bg-surface-danger text-danger',
};

export const Toast: React.FC<ToastProps> = ({ type, message, onDismiss }) => {
    const [visible, setVisible] = useState(false);
    const onDismissRef = useRef(onDismiss);
    // keep ref current to avoid capturing stale onDismiss in the effect
    onDismissRef.current = onDismiss;

    useEffect(() => {
        const showTimer = requestAnimationFrame(() => setVisible(true));
        const hideTimer = setTimeout(() => setVisible(false), DISMISS_DELAY_MS);
        const removeTimer = setTimeout(() => onDismissRef.current(), DISMISS_DELAY_MS + TRANSITION_MS);
        return () => {
            cancelAnimationFrame(showTimer);
            clearTimeout(hideTimer);
            clearTimeout(removeTimer);
        };
    }, []);

    // The Banner's tinted surface is translucent, so an opaque bg-surface sits underneath it:
    // a toast floats over the page and would otherwise let the content behind it show through.
    return (
        <div
            data-testid={`toast-${type}`}
            className={`max-w-sm rounded-lg bg-surface shadow-strong transition-all duration-300 ease-out ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
            }`}
            role="status"
            aria-live="polite"
        >
            <div className={`flex items-center gap-m rounded-lg border px-l py-m text-xs font-medium ${TONE_CLASSES[type]}`}>
                <BannerIcon tone={BANNER_TONE[type]} />
                {message}
            </div>
        </div>
    );
};
